<template>
    <common-page-plain>
        <common-calendar
            :key="calendarEventKey"
            ref="calendar"
            :topEvents="topCalendarEvents"
            :bottomEvents="bottomCalendarEvents"
            :innerWidth="innerWidth"
            :allowGoingBackward="true"
            :showShare="false"
            :availableUsers="availabilityPerformers"
            :selectedUserId="selectedPerformerId"
            :selectedDate="selectedDate"
            :showMonthDaySwitch="true"
            :excludedOfferIds="excludedOfferIds"
            @date-change="dateChange"
            @modal-date-change="modalDateChange"
            @user-selected="userSelected"
        >
            <template #dropdown>
                <calendar-events
                    :offers="selectedOffers"
                    :selectedDate="selectedDate"
                    :availabilityOffers="availabilityOffers"
                    @status-changed="calendarEventChanged"
                ></calendar-events>
                <div class="my-calendar__show-daily-calendar">
                    <span>{{ $t('common.microcopy.date') }}: {{ selectedDateFormatted }}</span>
                    <common-buttons>
                        <common-button
                            type="primary"
                            size="tiny"
                            :label="$t('common.microcopy.preview_daily_schedule')"
                            @click="goToDailySchedule()"
                        ></common-button>
                    </common-buttons>
                </div>
            </template>
            <template #left-side>
                <div v-if="$refs.calendar?.selectedCalendarType === CalendarTypeEnum.MONTLY" class="my-calendar__filter">
                    <multi-select-input
                        v-if="userOfferOptions.length"
                        class="my-calendar__filter--select"
                        :label="$t('pages.calendar.my_calendar.offer_user')"
                        :placeholder="$t('common.microcopy.select')"
                        :options="userOfferOptions"
                        :value="selectedUserOfferOptions"
                        :showSelectAllOption="true"
                        @change="setUserOptions"
                    ></multi-select-input>
                    <multi-select-input
                        v-if="companyOfferOptions.length"
                        class="my-calendar__filter--select"
                        :label="$t('pages.calendar.my_calendar.offer_company')"
                        :placeholder="$t('common.microcopy.select')"
                        :options="companyOfferOptions"
                        :value="selectedCompanyOfferOptions"
                        :showSelectAllOption="true"
                        :hasExpandableOptions="true"
                        @change="setCompanyOptions"
                    ></multi-select-input>
                </div>
            </template>
        </common-calendar>

        <common-modal v-if="isChooseTimeslotModalOpen" :showCloseIcon="true" width="wide" @close="closeChooseTimeslotModal">
            <div class="my-calendar__show-daily-calendar">
                <div class="my-calendar__show-daily-calendar--action">
                    <common-button
                        class="my-calendar__show-daily-calendar--action-button"
                        type="primary"
                        size="tiny"
                        :label="$t('common.microcopy.preview_daily_schedule')"
                        @click="goToDailySchedule()"
                    ></common-button>
                    <common-icon
                        name="refresh"
                        class="my-calendar__show-daily-calendar--action-icon"
                        size="huge"
                        @click="reloadData()"
                    ></common-icon>
                </div>
                <span>{{ $t('common.microcopy.date') }}: {{ selectedDateFormatted }}</span>
            </div>

            <calendar-events
                ref="calendarEvents"
                :offers="selectedOffers"
                :selectedDate="selectedDate"
                :availabilityOffers="availabilityOffers"
                @status-changed="calendarEventChanged"
            ></calendar-events>
        </common-modal>
    </common-page-plain>
</template>

<script>
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import AvailabilityAPI from 'api/AvailabilityAPI'
    import CalendarEvents from 'components/page-elements/calendar/CalendarEvents'
    import MultiSelectInput from 'components/form-inputs/MultiSelectInput'
    import OfferAPI from 'api/OfferAPI'
    import cloneDeep from 'lodash.clonedeep'
    import ReservationStatusEnum from 'enums/reservation_status.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import dayjs from 'dayjs'
    import CalendarTypeEnum from 'enums/calendar_type.enum'

    export default {
        mixins: [WindowResizeMixin],

        components: { CalendarEvents, MultiSelectInput },

        data: () => ({
            dateFrom: null,
            dateTo: null,
            schedule: [],
            // selectedOffers: [],
            selectedUserOfferOptions: [],
            selectedCompanyOfferOptions: [],
            userOfferOptions: [],
            allUserOfferIds: [],
            allCompanyOfferIds: [],
            companyOfferOptions: [],
            availabilityOffers: [],
            selectedDate: undefined,
            calendarEventKey: true,
            innerWidth: 0,
            innerHeight: 0,
            isChooseTimeslotModalOpen: false,
            selectedPerformerId: undefined,
            availabilityPerformers: [],
            excludedOfferIds: [],
            CalendarTypeEnum,
        }),

        computed: {
            topCalendarEvents() {
                return {
                    titlePath: 'pages.calendar.my_calendar.reservations',
                    dates: this.schedule.map((date) => ({
                        date: date.date,
                        count: date.offers
                            .reduce((acc, ao) => [...acc, ...ao.slots], [])
                            .filter((slot) => slot.calendarEventId && slot.status !== ReservationStatusEnum.UNAVAILABLE).length,
                    })),
                }
            },
            bottomCalendarEvents() {
                return {
                    titlePath: 'pages.calendar.my_calendar.services',
                    dates: this.schedule.map((date) => ({
                        date: date.date,
                        count: date.offers.length,
                    })),
                }
            },

            allOfferOptionIds() {
                return [...this.allUserOfferIds, ...this.allCompanyOfferIds]
            },

            selectedDateFormatted() {
                if (this.selectedDate) {
                    return dayjs(this.selectedDate).format('DD.MM.YYYY')
                }
                return undefined
            },

            selectedOffers() {
                if (!this.selectedDate) {
                    return []
                }
                return this.schedule.find((scheduleDate) => scheduleDate.date === this.selectedDate)?.offers || []
            },
        },

        methods: {
            dateChange({ dateFrom, dateTo }) {
                if (dateFrom !== this.dateFrom || dateTo !== this.dateTo) {
                    this.dateFrom = dateFrom
                    this.dateTo = dateTo
                    this.loadScheduleFiltered()
                }
            },

            setUserOptions({ value }) {
                this.selectedUserOfferOptions = value
                this.loadScheduleFiltered()
            },

            setCompanyOptions({ value }) {
                this.selectedCompanyOfferOptions = value
                this.loadScheduleFiltered()
            },

            loadScheduleFiltered() {
                this.excludedOfferIds = this.allOfferOptionIds.filter(
                    (el) => !this.selectedUserOfferOptions.includes(el) && !this.selectedCompanyOfferOptions.includes(el),
                )
                this.loadSchedule(this.dateFrom, this.dateTo, this.excludedOfferIds)
            },

            calendarEventChanged(payload) {
                if (payload !== ReservationStatusEnum.UNAVAILABLE) {
                    this.loadMyOffers()
                } else {
                    this.reloadData()
                }
            },

            modalDateChange(payload) {
                if (!payload.date) {
                    return
                }
                this.isChooseTimeslotModalOpen = true
                this.selectedDate = payload.date
            },

            async loadSchedule(dateFrom, dateTo, excludeOfferIds) {
                this.$startLoader()
                return CalendarEventAPI.getMySchedule({
                    dateFrom,
                    dateTo,
                    excludeOfferIds,
                    performerId: this.selectedPerformerId,
                })
                    .then((res) => {
                        this.schedule = res.dates || []
                        this.availabilityOffers = res.availabilityOffers || []
                        this.$stopLoader()
                    })
                    .finally(() => {
                        this.$stopLoader()
                    })
            },

            loadMyOffers() {
                OfferAPI.getMy().then((offers) => {
                    this.userOfferOptions = offers.personalOffers.map((offer) => ({
                        label: offer.titleTextContent.originalText,
                        value: offer.id,
                    }))
                    this.selectedUserOfferOptions = this.userOfferOptions.map((option) => option.value)
                    this.allUserOfferIds = cloneDeep(this.selectedUserOfferOptions)

                    this.companyOfferOptions = offers.companies.map((company) => ({
                        heading: company.name,
                        value: company.id,
                        options: company.offers.map((offer) => ({
                            label: offer.titleTextContent.originalText,
                            value: offer.id,
                        })),
                    }))
                    this.selectedCompanyOfferOptions = this.companyOfferOptions.reduce((acc, companies) => {
                        return [...acc, ...companies.options.map((option) => option.value)]
                    }, [])
                    this.allCompanyOfferIds = cloneDeep(this.selectedCompanyOfferOptions)
                    this.calendarEventKey = !this.calendarEventKey
                })
            },

            loadAvailabilityPerformers() {
                const currentUser = this.$auth.getUser()
                this.selectedPerformerId = currentUser.id
                AvailabilityAPI.getAvailabilityPerformers().then((res) => {
                    const isCurrentUserPerformer = res.find((u) => u.id === currentUser.id)
                    if (!isCurrentUserPerformer) {
                        this.availabilityPerformers = [...res, currentUser]
                    } else {
                        this.availabilityPerformers = res
                    }
                })
            },

            setBounderis() {
                const el = document.getElementsByClassName('calendar')[0]
                if (el) {
                    this.innerWidth = el.clientWidth + 50
                }
            },

            windowResize() {
                this.setBounderis()
            },

            closeChooseTimeslotModal() {
                this.isChooseTimeslotModalOpen = false
            },

            userSelected(userId) {
                this.selectedPerformerId = userId
                this.loadScheduleFiltered()
                const payload = {
                    value: this.$refs.calendar?.selectedCalendarType,
                }
                this.$refs.calendar.toggleMonthDayView(payload)
            },

            goToDailySchedule() {
                this.isChooseTimeslotModalOpen = false
                const payload = {
                    value: CalendarTypeEnum.DAILY,
                }
                this.$refs.calendar.toggleMonthDayView(payload)
            },

            reloadData() {
                this.$startLoader()
                return CalendarEventAPI.getMySchedule({
                    dateFrom: this.selectedDate,
                    dateTo: this.selectedDate,
                    excludeOfferIds: this.excludeOfferIds,
                    performerId: this.selectedPerformerId,
                })
                    .then((res) => {
                        const index = this.schedule.findIndex((item) => item.date === res.dates[0].date)
                        if (index !== -1) {
                            this.schedule[index] = res.dates[0]
                        }
                        this.$stopLoader()
                    })
                    .finally(() => {
                        this.$stopLoader()
                    })
            },
        },

        mounted() {
            this.selectedPerformerId = this.$auth.getUser().id
            this.loadMyOffers()
            this.loadAvailabilityPerformers()
            this.setBounderis()
        },
    }
</script>
<style lang="scss" scoped>
    @import 'styles/components/calendar/_my-calendar.scss';

    .my-calendar__show-daily-calendar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 14px;
        padding: 10px;
        flex-wrap: wrap;

        &--action {
            display: flex;
            align-items: center;
            &-button {
                margin-right: 15px;
            }
            &-icon {
                cursor: pointer;
            }
        }

        @include breakpoint(tablet) {
            padding: 0 15px 0 10px;
        }

        @include breakpoint(smartphone-large) {
            justify-content: center;
            flex-direction: column;
        }
    }
</style>
